import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Spinner } from '@/ui'
import { AppShell } from './components/AppShell.js'
import { AuthGate } from './components/AuthGate.js'
import { SectionGate } from './components/SectionGate.js'

// Eager imports for lighter pages.
import { DashboardPage } from '@/pages/DashboardPage.js'
import { ClientsPage } from '@/pages/ClientsPage.js'
import { ClientDetailPage } from '@/pages/ClientDetailPage.js'
import { JobcardsPage } from '@/pages/JobcardsPage.js'
import { AccountingHubPage } from '@/pages/AccountingHubPage.js'
import { CatalogPage } from '@/pages/CatalogPage.js'
import { StoragePage } from '@/pages/StoragePage.js'
import { StorageLevelPage } from '@/pages/StorageLevelPage.js'
import { SettingsPage } from '@/pages/SettingsPage.js'
import { StaffPage } from '@/pages/StaffPage.js'
import { SuppliersPage } from '@/pages/SuppliersPage.js'
import { WorkerDetailPage } from '@/pages/WorkerDetailPage.js'
import { CalendarPage } from '@/pages/CalendarPage.js'
import { PublicJobcardPage } from '@/pages/PublicJobcardPage.js'
import { JobCardPrintView } from '@/pages/JobCardPrintView.js'
import { ClientStatementPrintView } from '@/pages/ClientStatementPrintView.js'
import { InspectionReportPage } from '@/pages/InspectionReportPage.js'
import { InspectionPrintView } from '@/pages/InspectionPrintView.js'
import { InspectionTemplatePage } from '@/pages/InspectionTemplatePage.js'

// Lazy import for the heaviest page.
const JobcardEditorPage = lazy(() =>
  import('@/pages/JobcardEditorPage.js').then(m => ({ default: m.JobcardEditorPage }))
)

/** Not Found page. */
function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="text-center max-w-md">
        <h1 className="text-display-md text-on-surface mb-2">404</h1>
        <p className="text-body-lg text-on-surface-variant mb-6">Page not found</p>
        <a href="/" className="text-primary hover:underline font-semibold">
          Go to dashboard
        </a>
      </div>
    </div>
  )
}

/**
 * Route configuration.
 *
 * Staff routes are nested inside AuthGate (session required) and then
 * AppShell (persistent navigation frame). The public portal route
 * `/p/:token` is a SIBLING of AuthGate, never nested inside it: the customer
 * portal listener serves this exact same built bundle to reach that one
 * route, on a database token, never a staff session — nesting it under
 * AuthGate would show a customer a staff login screen.
 *
 * JobcardEditorPage is lazy-loaded; others are eager.
 */
export function App() {
  return (
    <Routes>
      {/* Public portal route: outside AuthGate and AppShell, no sidebar, no session */}
      <Route path="/p/:token" element={<PublicJobcardPage />} />

      <Route element={<AuthGate />}>
        {/* Printable job card: its own plain page, no sidebar or app chrome */}
        <Route path="/jobcards/:id/print" element={<SectionGate section="jobcards"><JobCardPrintView /></SectionGate>} />
        <Route path="/jobcards/:id/inspection/print" element={<SectionGate section="jobcards"><InspectionPrintView /></SectionGate>} />
        <Route path="/clients/:id/statement" element={<SectionGate section="clients"><ClientStatementPrintView /></SectionGate>} />

        {/* Staff routes: nested inside AppShell */}
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />

          <Route path="clients" element={<SectionGate section="clients"><ClientsPage /></SectionGate>} />
          <Route path="clients/:id" element={<SectionGate section="clients"><ClientDetailPage /></SectionGate>} />

          <Route path="jobcards" element={<SectionGate section="jobcards"><JobcardsPage /></SectionGate>} />
          <Route
            path="jobcards/new"
            element={
              <SectionGate section="jobcards">
                <Suspense fallback={<SpinnerFallback />}>
                  <JobcardEditorPage />
                </Suspense>
              </SectionGate>
            }
          />
          <Route
            path="jobcards/:id"
            element={
              <SectionGate section="jobcards">
                <Suspense fallback={<SpinnerFallback />}>
                  <JobcardEditorPage />
                </Suspense>
              </SectionGate>
            }
          />

          <Route path="finance" element={<Navigate to="/accounting?tab=overview" replace />} />
          <Route path="accounting" element={<SectionGate anySection={['finance', 'accounting']}><AccountingHubPage /></SectionGate>} />

          <Route path="staff" element={<SectionGate section="workers"><StaffPage /></SectionGate>} />
          <Route path="staff/:id" element={<SectionGate section="workers"><WorkerDetailPage /></SectionGate>} />

          <Route path="calendar" element={<SectionGate section="calendar"><CalendarPage /></SectionGate>} />

          <Route path="catalog" element={<SectionGate section="catalog"><CatalogPage /></SectionGate>} />
          <Route path="suppliers" element={<SectionGate section="suppliers"><SuppliersPage /></SectionGate>} />
          <Route path="storage" element={<SectionGate section="storage"><StoragePage /></SectionGate>} />
          <Route path="storage/levels/:id" element={<SectionGate section="storage"><StorageLevelPage /></SectionGate>} />
          <Route path="jobcards/:id/inspection" element={<SectionGate section="jobcards"><InspectionReportPage /></SectionGate>} />
          <Route path="settings" element={<SectionGate section="settings"><SettingsPage /></SectionGate>} />
          <Route path="settings/inspection-checklist" element={<SectionGate section="settings"><InspectionTemplatePage /></SectionGate>} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  )
}

/** Spinner fallback for lazy-loaded pages. */
function SpinnerFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner size="md" />
    </div>
  )
}
